import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import {
  GetCommunityPostRepliesDocument,
  GetCommunityPostRepliesQuery,
  GetCommunityPostRepliesQueryVariables,
  useGetCommunityPostRepliesQuery,
} from 'generated/graphql';
import { useAsyncQueryRaw } from '@client/new/helpers/gql';
import { Async, toAsyncFetched } from '@client/new/helpers/types';
import { toAuthorId } from '../model/post.types';
import { Reply, ReplyId, toReplyId } from '../model/reply.types';
import { CommunityId } from '../model/community.types';

type ServerPost = NonNullable<GetCommunityPostRepliesQuery['post']>;
type ServerReply = ServerPost['replies'][number];

const REPLIES_PAGE_SIZE = 25;
const REPLIES_POLL_INTERVAL_MS = 30_000;
const OLDER_PAGE_DIRECTION = 'olderThan';

export type CommunityRepliesResult = {
  replies: Async<Reply[]>;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
};

const toReply = (raw: ServerReply): Reply => ({
  id: toReplyId(raw.id),
  author: {
    id: toAuthorId(raw.author.id),
    name: raw.author.username,
    avatarUrl: raw.author.profilePicture,
    email: raw.author.email || null,
    isOwn: (raw.author.badges ?? []).includes('own'),
    isCreator: (raw.author.badges ?? []).includes('creator'),
    canReceiveDm: raw.author.hasDirectMessagesAccess === true,
  },
  createdAt: new Date(raw.createdAt),
  content: raw.text,
  reactions: raw.reactions.map((reaction) => ({
    type: reaction.type,
    count: reaction.count,
    isToggled: reaction.isToggled,
  })),
  isSensitive: raw.isSensitiveContent,
});

const toReplies = (data: GetCommunityPostRepliesQuery): Reply[] =>
  data.post ? data.post.replies.map(toReply) : [];

const dedupeById = (replies: Reply[]): Reply[] => {
  const seen = new Set<ReplyId>();
  const result: Reply[] = [];
  for (const reply of replies) {
    if (seen.has(reply.id)) continue;
    seen.add(reply.id);
    result.push(reply);
  }
  return result;
};

export const useCommunityPostReplies = (
  communityId: CommunityId,
  postId: string,
  canModerate: boolean,
): CommunityRepliesResult => {
  const client = useApolloClient();
  const query = useGetCommunityPostRepliesQuery({
    variables: { communityId, postId, canModerate },
    fetchPolicy: 'cache-and-network',
    pollInterval: REPLIES_POLL_INTERVAL_MS,
  });

  const { data: firstPage } = useAsyncQueryRaw({ query, mapper: toReplies });

  // Older pages live outside the normalized cache; they reconcile on re-open.
  const [olderReplies, setOlderReplies] = useState<Reply[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);

  useEffect(() => {
    setOlderReplies([]);
    setIsLoadingMore(false);
    setReachedEnd(false);
  }, [communityId, postId, canModerate]);

  const serverReplies = firstPage.type === 'fetched' ? firstPage.value : [];
  const combinedNewestFirst = dedupeById([...serverReplies, ...olderReplies]);

  const replies: Async<Reply[]> =
    firstPage.type === 'fetched'
      ? toAsyncFetched([...combinedNewestFirst].reverse())
      : firstPage;

  const hasMore =
    firstPage.type === 'fetched' &&
    !reachedEnd &&
    serverReplies.length >= REPLIES_PAGE_SIZE;

  const loadMore = useCallback(() => {
    if (isLoadingMore || reachedEnd) return;
    if (firstPage.type !== 'fetched') return;
    const combined = dedupeById([...firstPage.value, ...olderReplies]);
    if (combined.length === 0) return;
    const lastId = combined[combined.length - 1].id;

    setIsLoadingMore(true);
    client
      .query<
        GetCommunityPostRepliesQuery,
        GetCommunityPostRepliesQueryVariables
      >({
        query: GetCommunityPostRepliesDocument,
        variables: {
          communityId,
          postId,
          canModerate,
          direction: OLDER_PAGE_DIRECTION,
          lastId,
        },
        fetchPolicy: 'no-cache',
      })
      .then(({ data }) => {
        const page = toReplies(data);
        if (page.length < REPLIES_PAGE_SIZE) setReachedEnd(true);
        if (page.length > 0) {
          setOlderReplies((prev) => dedupeById([...prev, ...page]));
        }
      })
      .catch(() => setReachedEnd(true))
      .finally(() => setIsLoadingMore(false));
  }, [
    client,
    communityId,
    postId,
    canModerate,
    isLoadingMore,
    reachedEnd,
    firstPage,
    olderReplies,
  ]);

  return { replies, hasMore, isLoadingMore, loadMore };
};
